import L from 'leaflet';
import { ErrorEvent as MapLibreErrorEvent } from 'maplibre-gl';

import { FEATURE_TO_SYNC_TYPE } from '@/shared/features/jobs/jobs.constants';
import {
  TBaseMapFallbackReason,
  TBaseMapUsage,
} from '@/shared/features/map/components/base-map-layer/base-map-layer.types';
import {
  MAP_ALL_MODE_CHANGE_FEATURE_TYPES,
  MAP_DEFAULT_CENTER,
  MAP_DEFAULT_FEATURE_TYPES,
  MAP_DEFAULT_REFRESH_INTERVAL,
  MAP_DEFAULT_TIME_RANGE,
  MAP_DEFAULT_ZOOM,
  MAP_FEATURE_TYPE_OPTIONS,
  MAP_PAGE_SIZE,
  MAP_SINGLE_LOCATION_ZOOM,
  MAP_TIME_RANGE_OPTIONS,
} from '@/shared/features/map/map.constants';
import {
  TMapMarkerSyncBucketKey,
  TMapMarkerSyncDeviceItem,
  TMapMarkerSyncSummary,
  TMapTimeRange,
  TStatusKey,
} from '@/shared/features/map/map.types';
import {
  DeviceSyncStatusEnum,
  DeviceSyncTypeEnum,
  FeatureEnum,
  GatewayFragment,
  JobFragment,
  JobStatusEnum,
  ReadJobsByDeviceIdsQueryVariables,
  SyncedDeviceFragment,
} from '@/shared/types/types.generated';
import { resolveCountryLabel } from '@/shared/utils/country/country';

export const parseRefreshInterval = () => {
  const raw = Number(import.meta.env.VITE_MAP_REFRESH_INTERVAL_MS);
  if (!Number.isFinite(raw) || raw < 0) {
    return MAP_DEFAULT_REFRESH_INTERVAL;
  }
  return Math.round(raw);
};

// MapLibre needs WebGL2 and Chrome dropped its software fallback, so GPU-less VDI
// sessions and policy-disabled hardware acceleration return null here.
export const isWebGl2Available = () => {
  try {
    return Boolean(document.createElement('canvas').getContext('webgl2'));
  } catch {
    return false;
  }
};

// Tile-scoped errors carry the failed tile and are one of many, so a single flaky request
// must not cost the basemap; a style-scoped one means it cannot render at all.
export const isFatalBaseMapError = (event: MapLibreErrorEvent) =>
  !('tile' in event);

export const getBaseMapUsage = (
  fallbackReason: TBaseMapFallbackReason | null,
): TBaseMapUsage => ({
  mode: fallbackReason ? 'raster' : 'vector',
  reason: fallbackReason ?? 'none',
});

export const normalizeRefreshInterval = (value: unknown) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return MAP_DEFAULT_REFRESH_INTERVAL;
  }
  return Math.round(parsed);
};

export const isMapTimeRange = (value: string): value is TMapTimeRange =>
  MAP_TIME_RANGE_OPTIONS.some((option) => option.value === value);

export const normalizeMapTimeRange = (value: unknown): TMapTimeRange => {
  if (typeof value !== 'string') {
    return MAP_DEFAULT_TIME_RANGE;
  }

  return isMapTimeRange(value) ? value : MAP_DEFAULT_TIME_RANGE;
};

export const normalizeMapFeatureTypes = (value: unknown): FeatureEnum[] => {
  if (!Array.isArray(value)) {
    return MAP_DEFAULT_FEATURE_TYPES;
  }

  return MAP_FEATURE_TYPE_OPTIONS.filter((option) => value.includes(option));
};

export const getMapFeatureScope = (selectedFeatureTypes: FeatureEnum[]) => {
  const isAllFeatureMode = !selectedFeatureTypes.length;

  return {
    isAllFeatureMode,
    effectiveFeatureTypes: isAllFeatureMode
      ? MAP_FEATURE_TYPE_OPTIONS
      : selectedFeatureTypes,
    changeFeatureTypes: isAllFeatureMode
      ? MAP_ALL_MODE_CHANGE_FEATURE_TYPES
      : selectedFeatureTypes,
  };
};

export const getSyncTypesForFeatures = (
  featureTypes: FeatureEnum[],
): DeviceSyncTypeEnum[] =>
  featureTypes
    .map((feature) => FEATURE_TO_SYNC_TYPE[feature])
    .filter((syncType): syncType is DeviceSyncTypeEnum => Boolean(syncType));

export const filterDevicesBySyncTypes = (
  syncStatuses: SyncedDeviceFragment[],
  syncTypes: DeviceSyncTypeEnum[],
): SyncedDeviceFragment[] => {
  const syncTypeSet = new Set(syncTypes);

  return syncStatuses.filter((device) =>
    (device.accumulatedSyncStatus?.details ?? []).some((detail) =>
      syncTypeSet.has(detail.syncType),
    ),
  );
};

const MAP_TIME_RANGE_MS: Record<TMapTimeRange, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
};

export const getMapTimeRangeStartTimestamp = (
  range: TMapTimeRange,
  now = Date.now(),
) => new Date(now - MAP_TIME_RANGE_MS[range]).toISOString();

export const getBackupJobsQueryArgs = (
  deviceIds: string[],
  timeRange: TMapTimeRange,
  now: number,
  featureTypes: FeatureEnum[],
): ReadJobsByDeviceIdsQueryVariables => ({
  deviceIds,
  jobTypes: featureTypes,
  filter: {
    creationTimestamp: { gte: getMapTimeRangeStartTimestamp(timeRange, now) },
  },
  pagination: {
    page: 1,
    pageSize: MAP_PAGE_SIZE,
  },
});

const SYNC_STATUS_SEVERITY: Record<DeviceSyncStatusEnum, number> = {
  [DeviceSyncStatusEnum.Unknown]: 0,
  [DeviceSyncStatusEnum.Synced]: 1,
  [DeviceSyncStatusEnum.NotSynced]: 2,
  [DeviceSyncStatusEnum.Error]: 3,
};

const getDeviceSyncStatusForSyncTypes = (
  device: SyncedDeviceFragment,
  syncTypes: Set<DeviceSyncTypeEnum>,
): DeviceSyncStatusEnum | undefined =>
  (device.accumulatedSyncStatus?.details ?? []).reduce<
    DeviceSyncStatusEnum | undefined
  >((worst, detail) => {
    const status = detail.targetSyncStatus;
    if (!status || !syncTypes.has(detail.syncType)) {
      return worst;
    }
    if (!worst || SYNC_STATUS_SEVERITY[status] > SYNC_STATUS_SEVERITY[worst]) {
      return status;
    }
    return worst;
  }, undefined);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getDeviceSyncStatuses = (job: JobFragment) => {
  if (!isRecord(job.metaData || job.metaData.device_sync_statuses)) {
    return null;
  }

  const syncStatuses = job.metaData.device_sync_statuses;

  return isRecord(syncStatuses) ? syncStatuses : null;
};

const hasDeviceSyncStatusChanged = (deviceSyncStatus: unknown) => {
  if (!isRecord(deviceSyncStatus)) {
    return false;
  }

  const previousSyncStatus =
    deviceSyncStatus.previous_sync_status ??
    deviceSyncStatus.previousSyncStatus;
  const targetSyncStatus =
    deviceSyncStatus.target_sync_status ?? deviceSyncStatus.targetSyncStatus;

  return Boolean(
    previousSyncStatus &&
    targetSyncStatus &&
    previousSyncStatus !== targetSyncStatus,
  );
};

export const getDeviceBackupChangeCountById = (jobs: JobFragment[]) => {
  const changeCountByDeviceId: Record<string, number> = {};

  jobs.forEach((job) => {
    if (job.status !== JobStatusEnum.Success) {
      return;
    }

    const syncStatuses = getDeviceSyncStatuses(job);
    const deviceIds = new Set([
      ...(job.deviceIds ?? []),
      ...(syncStatuses ? Object.keys(syncStatuses) : []),
    ]);

    deviceIds.forEach((deviceId) => {
      const changeCount = hasDeviceSyncStatusChanged(syncStatuses?.[deviceId])
        ? 1
        : 0;

      changeCountByDeviceId[deviceId] =
        (changeCountByDeviceId[deviceId] ?? 0) + changeCount;
    });
  });

  return changeCountByDeviceId;
};

export const getGatewayBackupChangeCount = (
  syncStatuses: SyncedDeviceFragment[],
  deviceBackupChangeCountById: Record<string, number>,
) =>
  syncStatuses.reduce(
    (total, device) =>
      total + (deviceBackupChangeCountById[device.deviceId] ?? 0),
    0,
  );

export const getGatewayDeviceItems = (
  syncStatuses: SyncedDeviceFragment[],
): TMapMarkerSyncDeviceItem[] =>
  syncStatuses.map((device) => ({
    deviceId: device.deviceId,
    name: device.name,
  }));

export const getGatewaySyncSummary = (
  syncStatuses: SyncedDeviceFragment[],
  syncTypes: DeviceSyncTypeEnum[],
): TMapMarkerSyncSummary => {
  const syncTypeSet = new Set(syncTypes);

  return syncStatuses.reduce<TMapMarkerSyncSummary>(
    (acc, device) => {
      const syncStatus = getDeviceSyncStatusForSyncTypes(device, syncTypeSet);
      const syncDevice: TMapMarkerSyncDeviceItem = {
        deviceId: device.deviceId,
        name: device.name,
      };
      const pushToBucket = (bucketKey: TMapMarkerSyncBucketKey) => {
        acc.buckets[bucketKey].push(syncDevice);
      };

      switch (syncStatus) {
        case DeviceSyncStatusEnum.Synced:
          acc.syncedCount += 1;
          pushToBucket('synced');
          break;
        case DeviceSyncStatusEnum.NotSynced:
          acc.notSyncedCount += 1;
          pushToBucket('not_synced');
          break;
        case DeviceSyncStatusEnum.Error:
          acc.errorCount += 1;
          pushToBucket('error');
          break;
        default:
          acc.unknownCount = (acc.unknownCount ?? 0) + 1;
          pushToBucket('unknown');
          break;
      }

      return acc;
    },
    {
      syncedCount: 0,
      notSyncedCount: 0,
      errorCount: 0,
      unknownCount: 0,
      buckets: {
        synced: [],
        not_synced: [],
        error: [],
        unknown: [],
      },
    },
  );
};

export const getGatewayWorstStatus = (maxChangeCount: number): TStatusKey => {
  if (maxChangeCount >= 10) {
    return 'red';
  }

  if (maxChangeCount >= 5) {
    return 'yellow';
  }

  return 'green';
};

export const getCoordinatesFromGateway = (
  gateway: GatewayFragment,
): [number, number] | null => {
  const location = gateway.location;
  if (!location) {
    return null;
  }
  if (
    !Number.isFinite(location.latitudeE6) ||
    !Number.isFinite(location.longitudeE6)
  ) {
    return null;
  }
  return [location.latitudeE6 / 1_000_000, location.longitudeE6 / 1_000_000];
};

export const formatLocationLabel = (
  gateway: GatewayFragment,
  t: (key: string, options?: { defaultValue?: string }) => string,
) => {
  const location = gateway.location;
  if (!location) {
    return null;
  }
  const parts = [
    location.city,
    location.state,
    resolveCountryLabel(location.country, t),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
};

export const getLastSeenTimestamp = (gateway: GatewayFragment) => {
  return (
    gateway.state?.reportedAt ??
    gateway.publicIp?.reportedAt ??
    gateway.metadata?.reportedAt ??
    gateway.updateTimestamp ??
    gateway.creationTimestamp ??
    null
  );
};

export const getGatewayOsLabel = (gateway: GatewayFragment) => {
  return gateway.metadata?.operatingSystem ?? null;
};

export const getGatewayVersion = (gateway: GatewayFragment) => {
  return gateway.metadata?.version ?? null;
};

export const getGatewayHealthState = (gateway: GatewayFragment) => {
  return gateway.state?.state ?? null;
};

export const resetMapView = (
  map: L.Map,
  markerCoordinates: [number, number][],
): void => {
  if (!markerCoordinates.length) {
    map.setView(MAP_DEFAULT_CENTER, MAP_DEFAULT_ZOOM);
    return;
  }

  const uniqueLocationCount = new Set(
    markerCoordinates.map(
      (coords) => `${coords[0].toFixed(6)},${coords[1].toFixed(6)}`,
    ),
  ).size;

  if (uniqueLocationCount === 1) {
    map.setView(markerCoordinates[0], MAP_SINGLE_LOCATION_ZOOM);
    return;
  }

  map.fitBounds(markerCoordinates, { padding: [30, 30] });
};
